import React, { FormEventHandler, useCallback, useEffect, useRef } from 'react'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { formToObject } from 'lib/forms/formToObject'
import { forgotPassword, login } from 'actions/AuthActions'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import Group from 'components/utils/Group'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { connect } from 'react-redux'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import noop from 'lib/function/noop'
import { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { selectLoggedIn } from 'selectors/accountSelectors'

interface FormData {
  email: string
  password: string
}

interface MappedStateProps {
  authError?: App.ApiCallFailure
  isAuthProcessing?: boolean
}

interface Props {
  user: App.User;
  onModeChange?: (mode: App.UiAccountModalMode) => void;
}

function BusinessTravellerPasswordLoginForm(props: Props & MappedStateProps) {
  const {
    authError,
    isAuthProcessing,
    user,
    onModeChange = noop,
  } = props

  const appDispatch = useAppDispatch()

  const formRef = useRef<HTMLFormElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const loggedIn = useAppSelector(selectLoggedIn)

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>(async(event) => {
    event.preventDefault()
    const formData = formToObject<FormData>(event.currentTarget)
    const recaptchaResponse = await recaptchaRef.current?.executeAsync()
    if (recaptchaResponse) {
      appDispatch(login({ email: formData.email }, formData.password, recaptchaResponse))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  const handleForgotPassword = useCallback(() => {
    appDispatch(forgotPassword(user.email || ''))
    formRef.current?.reset()
    onModeChange('forgotPassword')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onModeChange])

  useEffect(() => {
    passwordInputRef.current?.focus()
  }, [])
  const onBack = useCallback(() => {
    onModeChange('login')
  }, [onModeChange])

  return <>
    <ModalHeader
      title=""
      onBackButtonClick={onBack}
    />
    <ModalBody>
      <ModalContent>
        <form
          ref={formRef}
          name="businessTravellerPasswordLoginForm"
          data-testid="business-traveller-password-login-form"
          onSubmit={handleSubmission}
        >
          <Group direction="vertical" gap={20}>
            {!!authError?.message && <MessageBanner
              description={authError.message}
              kind="critical"
            />}

            <Group direction="vertical" gap={8}>
              <Heading variant="heading4">
                Welcome back
              </Heading>
              <BodyText variant="medium">
                Please enter your password below to continue.
              </BodyText>
            </Group>

            <Group direction="vertical">
              <HiddenInput
                name="email"
                value={user.email}
              />
              <PasswordInput
                label="Password"
                required
                name="password"
                placeholder="Password"
                requiredErrorMessage="Password is required"
                ref={passwordInputRef}
              />

              <Group direction="vertical" gap={16}>
                <TextButton
                  kind="primary"
                  size="large"
                  type="submit"
                  disabled={isAuthProcessing}
                >
                  Log in
                </TextButton>

                <Group direction="horizontal" horizontalAlign="center">
                  <TextLink
                    size="medium"
                    onClick={handleForgotPassword}
                  >
                    Forgot your password?
                  </TextLink>
                </Group>
              </Group>
            </Group>
          </Group>
          {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={recaptchaRef} />}
        </form>
      </ModalContent>
    </ModalBody>
  </>
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    authError: state.auth.error,
    isAuthProcessing: state.auth.processing,
  }
})(BusinessTravellerPasswordLoginForm)
